package com.exura.features.plandefinition.domain.model.series

import com.exura.features.plandefinition.domain.model.element.PersistentElement
import dev.gitlive.firebase.firestore.CollectionReference

/**
 * A series that generates persistent elements.
 * Implements SerializableDomainModel for Firebase storage through BaseAnchoredSeries.
 */
data class SimplePersistentSeries(
    override val firebaseDocId: String? = null,
    override val firebaseDocPrefix: CollectionReference? = null,
    override val title: String,
    override val description: String,
    override val relativeDay: Int,
    override val elements: List<PersistentElement> = emptyList(),
    override val imageUrl: String? = null,
    val durationDays: Int? = null,
    val durationInMinutes: Int? = null,
    val startHourOfDay: HourOfDay? = null,
    val icon: String? = null,
) : BaseAnchoredSeries<PersistentElement>(
    firebaseDocId, firebaseDocPrefix, SeriesType.SIMPLE_PERSISTENT.spawnType,
) {

    init {
        // Ensure one and only one duration specification is provided
        require((durationDays == null) != (durationInMinutes == null)) {
            "Either durationDays or durationInMinutes must be specified, but not both"
        }
    }

    override fun spawnElements(): List<PersistentElement> {
        if (durationDays != null) {
            return spawnDayPrecisionElements(durationDays)
        } else {
            return spawnMinutePrecisionElements(durationInMinutes!!)
        }
    }

    private fun spawnDayPrecisionElements(days: Int): List<PersistentElement> {
        // Calculate end day based on whole days
        val endDay = relativeDay + days - 1

        return listOf(
            PersistentElement(
                startRelativeDay = relativeDay,
                endRelativeDay = endDay,
                startRelativeHourOfDay = startHourOfDay,
                endRelativeHourOfDay = null,
                title = title,
                description = description,
                icon = icon,
            )
        )
    }

    private fun spawnMinutePrecisionElements(minutes: Int): List<PersistentElement> {
        // Calculate number of full days
        val minutesInDay = 24 * 60
        val fullDays = minutes / minutesInDay
        val remainingMinutes = minutes % minutesInDay

        // Calculate end day
        val endDay = relativeDay + fullDays
        val startHOD = startHourOfDay ?: HourOfDay()
        val startMinutes = startHOD.hour * 60 + startHOD.minute
        val totalEndMinutes = startMinutes + remainingMinutes

        // Normalize to hours and minutes
        val endHour = (totalEndMinutes / 60) % 24
        val endMinute = totalEndMinutes % 60

        // Calculate end hour/minute based on start time and remaining minutes
        val endHourOfDay = HourOfDay(endHour, endMinute)

        return listOf(
            PersistentElement(
                startRelativeDay = relativeDay,
                endRelativeDay = endDay,
                startRelativeHourOfDay = startHourOfDay,
                endRelativeHourOfDay = endHourOfDay,
                title = title,
                description = description,
                icon = icon,
            )
        )
    }

    override fun durationInDays(): Int {
        // Return the explicitly specified duration if available
        if (durationDays != null) {
            return durationDays
        }

        // Otherwise calculate from durationInMinutes, rounding up to the nearest day
        val minutesInDay = 24 * 60
        val minutes = durationInMinutes ?: 0
        return (minutes + minutesInDay - 1) / minutesInDay
    }
}
